import { ClientStorage, MedplumClient, MemoryStorage } from '@medplum/core';

let botClient: MedplumClient | undefined;
let authPromise: Promise<void> | undefined;

/**
 * Returns a MedplumClient authenticated with the provider-app bot client credentials.
 * Used on public (unauthenticated) pages (e.g. PCP referral intake) to execute bots
 * without requiring user login.
 *
 * The client credentials are scoped via an AccessPolicy that only allows bot execution.
 *
 * The bot client is given its own in-memory storage so its `activeLogin` never lands in
 * `localStorage`. Otherwise, when a signed-in provider opens the intake flow, the bot's
 * client-credentials login would clobber the provider's active login under the shared
 * Medplum base URL — leaving them authenticated as the bot on the next navigation.
 */
export async function getIntakeBotClient(): Promise<MedplumClient> {
  if (botClient) {
    if (!authPromise) {
      return botClient;
    }
    await authPromise;
    return botClient;
  }

  const clientId = import.meta.env.VITE_PROVIDER_INTAKE_BOT_CLIENT_ID as string;
  const clientSecret = import.meta.env.VITE_PROVIDER_INTAKE_BOT_CLIENT_SECRET as string;

  if (!clientId || !clientSecret) {
    throw new Error('VITE_PROVIDER_INTAKE_BOT_CLIENT_ID and VITE_PROVIDER_INTAKE_BOT_CLIENT_SECRET must be configured');
  }

  const baseUrl = import.meta.env.VITE_MEDPLUM_BASE_URL as string | undefined;

  botClient = new MedplumClient({
    baseUrl,
    clientId,
    storage: new ClientStorage(new MemoryStorage()),
  });

  authPromise = botClient.startClientLogin(clientId, clientSecret).then(() => {
    authPromise = undefined;
  });

  await authPromise;
  return botClient;
}
